import { useState } from "react";
import ModalPortal from "../../../shared/components/ModalPortal";
import { useLang } from "../../../shared/i18n/useLang";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { descargarEstadoCuentaPDF } from "../services/portalService";

// Fechas en hora local (toISOString las correría a UTC).
function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * El residente descarga el estado de cuenta de su filial en PDF. El servidor
 * pone la filial (la suya), limita el rango a 5 años y la cantidad de PDFs por
 * hora; acá solo se escogen las fechas y el idioma.
 */
export default function EstadoCuentaModal({ onClose }) {
  const L = useLang();
  const hoy = new Date();
  const y = hoy.getFullYear();
  const ATAJOS = [
    { id: "mes",    label: L("Este mes", "This month"),           desde: iso(new Date(y, hoy.getMonth(), 1)), hasta: iso(hoy) },
    { id: "tres",   label: L("Últimos 3 meses", "Last 3 months"), desde: iso(new Date(y, hoy.getMonth() - 2, 1)), hasta: iso(hoy) },
    { id: "anio",   label: L("Este año", "This year"),            desde: `${y}-01-01`, hasta: iso(hoy) },
    { id: "pasado", label: L("Año pasado", "Last year"),          desde: `${y - 1}-01-01`, hasta: `${y - 1}-12-31` },
  ];
  const [atajo, setAtajo] = useState("anio");
  const [desde, setDesde] = useState(ATAJOS[2].desde);
  const [hasta, setHasta] = useState(ATAJOS[2].hasta);
  const [idioma, setIdioma] = useState(L("es", "en"));
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  function escoger(a) {
    setAtajo(a.id);
    setDesde(a.desde);
    setHasta(a.hasta);
    setError("");
  }

  async function descargar() {
    setError("");
    if (!desde || !hasta) { setError(L("Escogé las dos fechas.", "Pick both dates.")); return; }
    if (desde > hasta) { setError(L("La fecha inicial no puede ser después de la final.", "The start date can't be after the end date.")); return; }
    setCargando(true);
    try {
      await descargarEstadoCuentaPDF({ desde, hasta, idioma });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }

  // Va montado en <body> (ModalPortal): la página del portal entra con una
  // animación de transform y, escrito acá adentro, el `fixed` se centraba
  // contra el alto de la página y no contra la pantalla.
  const contenido = (
    <div className="ecm-overlay" {...overlayClose(onClose)}>
      <style>{CSS}</style>
      <div className="ecm-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="ecm-head">
          <div className="ecm-titulo">
            <span className="ecm-icono"><i className="fa-solid fa-file-invoice-dollar" /></span>
            <div>
              <h3>{L("Estado de cuenta", "Account statement")}</h3>
              <p>{L("Tus cargos y pagos en PDF", "Your charges and payments as PDF")}</p>
            </div>
          </div>
          <button type="button" className="ecm-x" onClick={onClose} aria-label={L("Cerrar", "Close")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="ecm-cuerpo">
          <label className="ecm-label">{L("Período", "Period")}</label>
          <div className="ecm-chips">
            {ATAJOS.map((a) => (
              <button type="button" key={a.id} className={`ecm-chip ${atajo === a.id ? "on" : ""}`} onClick={() => escoger(a)}>
                {a.label}
              </button>
            ))}
            <button type="button" className={`ecm-chip ${atajo === "otro" ? "on" : ""}`} onClick={() => setAtajo("otro")}>
              <i className="fa-regular fa-calendar" /> {L("Escoger fechas", "Pick dates")}
            </button>
          </div>
          {atajo === "otro" && (
            <div className="ecm-fechas">
              <label>{L("Desde", "From")}
                <input type="date" value={desde} max={iso(hoy)} onChange={(e) => setDesde(e.target.value)} />
              </label>
              <label>{L("Hasta", "To")}
                <input type="date" value={hasta} max={iso(hoy)} onChange={(e) => setHasta(e.target.value)} />
              </label>
            </div>
          )}

          <label className="ecm-label">{L("Idioma del documento", "Document language")}</label>
          <div className="ecm-chips">
            <button type="button" className={`ecm-chip ${idioma === "es" ? "on" : ""}`} onClick={() => setIdioma("es")}>Español</button>
            <button type="button" className={`ecm-chip ${idioma === "en" ? "on" : ""}`} onClick={() => setIdioma("en")}>English</button>
          </div>

          {error && <div className="ecm-error"><i className="fa-solid fa-triangle-exclamation" /> {error}</div>}
        </div>

        <div className="ecm-pie">
          <button type="button" className="ecm-btn" onClick={onClose} disabled={cargando}>{L("Cancelar", "Cancel")}</button>
          <button type="button" className="ecm-btn ecm-primario" onClick={descargar} disabled={cargando}>
            <i className={`fa-solid ${cargando ? "fa-circle-notch fa-spin" : "fa-download"}`} />
            {cargando ? L("Generando…", "Generating…") : L("Descargar PDF", "Download PDF")}
          </button>
        </div>
      </div>
    </div>
  );

  return <ModalPortal>{contenido}</ModalPortal>;
}

const CSS = `
.ecm-overlay { position: fixed; inset: 0; z-index: 1000; background: var(--overlay); display: flex; align-items: center; justify-content: center; padding: 16px; }
.ecm-modal { width: min(480px, 100%); background: var(--bg-card); border: 1px solid var(--border-card); border-radius: 16px; box-shadow: 0 24px 64px var(--shadow); }
.ecm-modal i { color: inherit; }
.ecm-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 16px 20px; border-bottom: 1px solid var(--border-card); }
.ecm-titulo { display: flex; align-items: center; gap: 12px; }
.ecm-titulo h3 { margin: 0; font-size: 1.05rem; font-weight: 700; color: var(--text-primary); }
.ecm-titulo p { margin: 2px 0 0; font-size: 0.8rem; color: var(--text-muted); }
.ecm-icono { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center;
  background: rgba(var(--brand-rgb), .15); border: 1px solid rgba(var(--brand-rgb), .35); color: var(--brand); font-size: 1.05rem; }
.ecm-x { width: 36px; height: 36px; border-radius: 9px; border: 1px solid var(--border-card); background: transparent; color: var(--text-secondary); cursor: pointer; }
.ecm-x:hover { color: var(--brand); border-color: var(--brand); }
.ecm-cuerpo { padding: 18px 20px; display: flex; flex-direction: column; gap: 10px; }
.ecm-label { margin: 6px 0 0; font-size: 0.8rem; font-weight: 600; color: var(--text-secondary); }
.ecm-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.ecm-chip { min-height: 40px; padding: 7px 14px; border-radius: 999px; cursor: pointer; font-size: 0.86rem; font-weight: 600;
  border: 1px solid var(--border-input); background: var(--bg-input); color: var(--text-primary); display: inline-flex; align-items: center; gap: 7px; }
.ecm-chip.on { border-color: var(--brand); background: rgba(var(--brand-rgb), .14); color: var(--brand); }
.ecm-fechas { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.ecm-fechas label { display: flex; flex-direction: column; gap: 5px; font-size: 0.78rem; color: var(--text-secondary); margin: 0; }
.ecm-fechas input { min-height: 42px; padding: 8px 10px; border-radius: 10px; font-size: 0.9rem; outline: none;
  background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-input); }
.ecm-error { display: flex; gap: 8px; align-items: flex-start; padding: 10px 12px; border-radius: 10px; font-size: 0.84rem;
  color: #f87171; background: rgba(239, 68, 68, .08); border: 1px solid rgba(239, 68, 68, .3); }
[data-theme="light"] .ecm-error { color: #b91c1c; }
.ecm-pie { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; padding: 14px 20px; border-top: 1px solid var(--border-card); }
.ecm-btn { min-height: 42px; padding: 9px 16px; border-radius: 10px; cursor: pointer; font-size: 0.88rem; font-weight: 600;
  display: inline-flex; align-items: center; gap: 8px; background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-card); }
.ecm-btn:disabled { opacity: .55; cursor: not-allowed; }
.ecm-primario { background: var(--brand); border-color: var(--brand); color: #fff; }
.ecm-primario:hover:not(:disabled) { background: var(--brand-deep); }
@media (max-width: 420px) { .ecm-fechas { grid-template-columns: 1fr; } }
`;
